import classNames from 'classnames';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import TooltipArrow from './tooltip-arrow/tooltip-arrow';
import styles from './tooltip.module.scss';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** The dom content of the tooltip */
  children?: React.ReactNode;
  /** Additional class name */
  className?: string;
  /** Optional tooltip content */
  content?: React.ReactNode;
  /** Disables the hover effect */
  disableHover?: boolean;
  /** Disables the click event on tooltip */
  disableTooltipClick?: boolean;
  /** The headline of the tooltip */
  headline?: string;
  /** Whether the tooltip is open */
  isOpen?: boolean;
  /** The position of the tooltip */
  position?: TooltipPosition;
  /** The text of the tooltip */
  text?: string;
  /** Indicates whether it applies minimal width to the tooltip */
  useMinimumWidth?: boolean;
  /** Indicates whether it applies tooltip to children directly */
  wrapChildrenOnly?: boolean;
}

const decorationClassNameMap: Record<TooltipPosition, string> = {
  bottom: styles.hasArrowTop,
  left: styles.hasArrowRight,
  right: styles.hasArrowLeft,
  top: styles.hasArrowBottom,
};

const transformMap: Record<TooltipPosition, CSSProperties> = {
  bottom: { left: '50%', transform: 'translate(-50%, 100%)' },
  left: { top: '50%', transform: 'translate(-100%, -50%)' },
  right: { top: '50%', transform: 'translate(100%, -50%)' },
  top: { left: '50%', transform: 'translate(-50%, -100%)' },
};

const Tooltip = ({
  children,
  content,
  className,
  disableHover,
  disableTooltipClick,
  headline,
  isOpen,
  position = 'top',
  text,
  useMinimumWidth = true,
  wrapChildrenOnly = false,
}: TooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});

  useEffect(() => {
    setTooltipStyle({
      [position]: -12,
      ...transformMap[position],
    });
  }, [position]);

  const tooltipContent = content ? (
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      data-testid="tooltip"
      style={tooltipStyle}
    >
      {content}

      <TooltipArrow className={styles.decoration} />
    </div>
  ) : (
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      data-testid="tooltip"
      style={tooltipStyle}
    >
      {headline && <Typography tagStyle="bodySmallBold">{headline}</Typography>}

      {text && <Typography tagStyle="bodySmall">{text}</Typography>}

      <TooltipArrow className={styles.decoration} />
    </div>
  );

  return (
    <div
      className={classNames(
        styles.component,
        className,
        decorationClassNameMap[position],
        {
          [styles.hasDisabledHover]: disableHover,
          [styles.hasMinimumWidth]: useMinimumWidth,
          [styles.hasDisabledClick]: disableTooltipClick,
          [styles.isOpen]: isOpen,
        },
      )}
    >
      {children}

      {wrapChildrenOnly ? (
        tooltipContent
      ) : (
        <div className={styles.tooltipAnchorWrapper}>
          {tooltipContent}

          <Icon name="Info" />
        </div>
      )}
    </div>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
